import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { CopyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import "../../components/common/H5PromptModal.css";
import { EasyAvatar } from "fin-web-components";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppAvatarUrl } from "../../utils/resolveAppMediaUrl";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import type { SpaceProfile } from "../SpaceType";
import { formatSpaceMetricValue } from "../spacePageUtils";
import { SPACE_HOME_AVATAR_CONFIG } from "./Config";
import { SpaceHomeCertLabel } from "./SpaceHomeCertLabel";
import { WeworkServiceApi } from "../../wework_service/WeworkServiceApi";
import { WeworkServiceModal } from "../../wework_service/WeworkServiceModal";
import type { WeworkServiceCardView } from "../../wework_service/WeworkServiceType";

/** 抖音式：粉色 Hero（头像+基本信息）+ 白色圆角底栏（数据/介绍/按钮 + 子内容） */
export function SpaceHomeInfoSection(props: {
  profile: SpaceProfile;
  isLoggedIn: boolean;
  favorStatus: 0 | 1;
  onLoginRequest: () => void;
  onFollow: () => void;
  onRequestUnfollow: () => void;
  onSubscribe: () => void;
  children?: ReactNode;
}) {
  const {
    profile,
    isLoggedIn,
    favorStatus,
    onLoginRequest,
    onFollow,
    onRequestUnfollow,
    onSubscribe,
    children,
  } = props;
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [weworkOpen, setWeworkOpen] = useState(false);
  const [weworkLoading, setWeworkLoading] = useState(false);
  const [weworkCard, setWeworkCard] = useState<WeworkServiceCardView | null>(null);
  const certLabel = profile.certLabelName?.trim() ?? "";
  const introText = profile.signature?.trim() ?? "";
  const avatarUrl = resolveAppAvatarUrl(profile.avatarUrl);
  const douyinHomeUrl = profile.douyinHomeUrl?.trim() ?? "";

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [menuOpen]);

  const showShareCopiedModal = useCallback((spaceName: string) => {
    const modal = Modal.info({
      className: "lf-h5-prompt-modal",
      icon: null,
      centered: true,
      width: 320,
      closable: true,
      maskClosable: true,
      footer: null,
      content: (
        <>
          <div className="lf-h5-prompt-modal__body">
            <p className="lf-h5-prompt-modal__title">
              您已经复制了「{spaceName}」的网址链接，快去分享吧。
            </p>
          </div>
          <div className="lf-h5-prompt-modal__actions lf-h5-prompt-modal__actions--single">
            <button
              type="button"
              className="lf-h5-prompt-modal__btn lf-h5-prompt-modal__btn--primary lf-h5-prompt-modal__btn--block"
              onClick={() => modal.destroy()}
            >
              我知道了
            </button>
          </div>
        </>
      ),
    });
  }, []);

  const onCopySpaceId = useCallback(() => {
    void copyTextToClipboard(profile.spaceId).then((ok) => {
      if (ok) {
        message.success("已复制龙粉号");
      } else {
        message.error("复制失败，请重试");
      }
    });
  }, [profile.spaceId]);

  const onGoPlatformHome = useCallback(() => {
    setMenuOpen(false);
    navigate("/");
  }, [navigate]);

  const onShareHome = useCallback(() => {
    setMenuOpen(false);
    const base = process.env.PUBLIC_URL || "";
    const url = `${window.location.origin}${base}/space/${profile.spaceId}/home`;
    void copyTextToClipboard(url).then((ok) => {
      if (ok) {
        showShareCopiedModal(profile.name);
      } else {
        message.error("复制失败，请重试");
      }
    });
  }, [profile.name, profile.spaceId, showShareCopiedModal]);

  const onAssistantWecom = useCallback(() => {
    setMenuOpen(false);
    if (!isLoggedIn) {
      message.info("请先登录后再联系助理企微");
      onLoginRequest();
      return;
    }
    setWeworkLoading(true);
    void WeworkServiceApi.getMyBound({ spaceId: profile.spaceId })
      .then((res) => {
        if (!res?.card?.qrcodeUrl?.trim()) {
          message.warning("暂未配置空间企业微信客服，请联系空间管理员");
          return;
        }
        setWeworkCard(res.card);
        setWeworkOpen(true);
      })
      .catch((e: unknown) => {
        message.error(e instanceof Error ? e.message : "加载失败");
      })
      .finally(() => {
        setWeworkLoading(false);
      });
  }, [isLoggedIn, onLoginRequest, profile.spaceId]);

  const onGoDouyin = useCallback(() => {
    setMenuOpen(false);
    if (!douyinHomeUrl) {
      message.warning("暂未配置抖音主页链接");
      return;
    }
    window.open(douyinHomeUrl, "_blank", "noopener,noreferrer");
  }, [douyinHomeUrl]);

  return (
    <>
      <header className="space-home-hero" aria-label="空间基本信息">
        <div className="space-home-top-bar">
          <div className="space-home-top-menu" ref={menuRef}>
            <button
              type="button"
              className="space-home-top-btn space-home-top-btn--menu"
              aria-label="更多操作"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="space-home-top-menu-icon" aria-hidden />
            </button>
            {menuOpen ? (
              <div className="space-home-top-menu-pop" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="space-home-top-menu-item"
                  onClick={onGoPlatformHome}
                >
                  龙粉社首页
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="space-home-top-menu-item"
                  onClick={onShareHome}
                >
                  分享主页
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="space-home-top-menu-item"
                  disabled={weworkLoading}
                  onClick={onAssistantWecom}
                >
                  助理企微
                </button>
                {douyinHomeUrl ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="space-home-top-menu-item"
                    onClick={onGoDouyin}
                  >
                    前往抖音号
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="space-home-hero-inner">
          <EasyAvatar
            shape="circle"
            size={SPACE_HOME_AVATAR_CONFIG.size}
            url={avatarUrl || undefined}
            word={avatarDisplayWord(profile.name, profile.spaceId)}
            alt=""
            className="space-home-avatar"
            border={{
              width: SPACE_HOME_AVATAR_CONFIG.borderWidth,
              color: SPACE_HOME_AVATAR_CONFIG.borderColor,
            }}
          />
          <div className="space-home-hero-text">
            <div className="space-home-name-row">
              <h1 className="space-home-name">{profile.name}</h1>
              {certLabel ? <SpaceHomeCertLabel name={certLabel} /> : null}
            </div>
            <div className="space-home-id-row">
              <span className="space-home-id-text">龙粉号：{profile.spaceId}</span>
              <button
                type="button"
                className="space-home-id-copy"
                aria-label="复制龙粉号"
                title="复制"
                onClick={onCopySpaceId}
              >
                <CopyOutlined />
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="space-home-sheet" aria-label="空间主页">
        <div className="space-home-sheet-head">
          <div className="space-home-metrics" aria-label="空间数据">
            <div className="space-home-metric">
              <span className="space-home-metric-value">
                {formatSpaceMetricValue(profile.followerCount)}
              </span>
              <span className="space-home-metric-label">粉丝</span>
            </div>
            <div className="space-home-metric">
              <span className="space-home-metric-value">
                {formatSpaceMetricValue(profile.subscriptionCount ?? 0)}
              </span>
              <span className="space-home-metric-label">订阅</span>
            </div>
          </div>

          {introText ? <p className="space-home-intro">{introText}</p> : null}

          <div className="space-home-actions">
            {isLoggedIn ? (
              favorStatus === 1 ? (
                <button
                  type="button"
                  className="space-home-btn space-home-btn--followed"
                  onClick={onRequestUnfollow}
                >
                  已关注
                </button>
              ) : (
                <button
                  type="button"
                  className="space-home-btn space-home-btn--follow"
                  onClick={onFollow}
                >
                  关注
                </button>
              )
            ) : (
              <button
                type="button"
                className="space-home-btn space-home-btn--follow"
                onClick={onLoginRequest}
              >
                关注
              </button>
            )}
            <button
              type="button"
              className="space-home-btn space-home-btn--subscribe"
              onClick={onSubscribe}
            >
              订阅
            </button>
          </div>
        </div>

        <div className="space-home-sheet-body">{children}</div>
      </section>
      <WeworkServiceModal
        open={weworkOpen}
        title="助理企微"
        content=""
        card={weworkCard}
        onClose={() => setWeworkOpen(false)}
      />
    </>
  );
}
